import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiAward, FiTrendingUp, FiImage, FiChevronLeft, FiChevronRight, FiEye, FiUsers, FiX, FiCalendar, FiExternalLink } from 'react-icons/fi';

const CollegeAchievements = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const achievements = [
    {
      id: 1,
      title: "Event Coordinator",
      event: "College Cultural Fest",
      date: "2024",
      description: "Successfully coordinated and managed the annual cultural festival with 500+ participants",
      fullDescription: "Led the organizing committee for the annual cultural festival, managing a team of 15 volunteers and coordinating with multiple departments. Successfully executed a 3-day event with 500+ participants, 20+ competitions, and guest performances. Handled logistics, vendor management, and ensured smooth execution of all events.",
      category: "Leadership",
      skills: ["Event Management", "Leadership", "Team Coordination", "Budget Planning"],
      certificateUrl: "/images/achievements/event-coordinator-certificate.png",
      images: [
        { url: "/images/achievements/fest-photo1.jpg", caption: "Opening ceremony" },
        { url: "/images/achievements/fest-photo2.jpg", caption: "Cultural performances" }
      ],
      impact: [
        { value: "500+", label: "Participants" },
        { value: "15", label: "Team Members" }
      ],
      badgeColor: "from-purple-500 to-pink-500",
      icon: FiUsers
    },
    {
      id: 2,
      title: "Top 10 Team",
      event: "College Hackathon", 
      date: "2025",
      description: "Achieved top 10 position with innovative SkillSwap web application among 50+ teams",
      fullDescription: "Developed 'SkillSwap', a peer-to-peer skill exchange platform where users can teach and learn skills from each other. Built using React.js, Node.js, and MongoDB. Implemented features like skill matching, scheduling, and user reviews. Secured top 10 position among 50+ participating teams.",
      category: "Innovation",
      skills: ["React.js", "Node.js", "MongoDB", "Problem Solving", "Teamwork"],
      certificateUrl: "/images/achievements/hackathon-certificate.png",
      images: [
        { url: "/images/achievements/skillswap-demo.jpg", caption: "SkillSwap app demo" },
        { url: "/images/achievements/team-photo.jpg", caption: "Our winning team" }
      ],
      impact: [
        { value: "Top 10", label: "Position" },
        { value: "50+", label: "Teams" }
      ],
      projectLink: "#projects",
      badgeColor: "from-orange-500 to-red-500",
      icon: FiTrendingUp
    },
    {
      id: 3,
      title: "Zscaler Hands-on Lab",
      event: "Cybersecurity Seminar",
      date: "2025",
      description: "Completed intensive hands-on cybersecurity workshop and practical lab session",
      fullDescription: "Participated in a comprehensive cybersecurity workshop conducted by Zscaler experts. Gained hands-on experience with cloud security solutions, zero-trust architecture, and network security protocols. Completed practical lab exercises on threat detection and secure web gateways.",
      category: "Learning",
      skills: ["Cloud Security", "Zero Trust", "Network Security", "Threat Detection"],
      certificateUrl: "/images/achievements/zscaler-seminar-certificate.png",
      images: [
        { url: "/images/achievements/zscaler-lab.jpg", caption: "Hands-on lab session" },
        { url: "/images/achievements/myimage.jpg", caption: "Personal achievement" }
      ],
      impact: [
        { value: "8 hrs", label: "Lab Session" },
        { value: "100%", label: "Completion" }
      ],
      badgeColor: "from-blue-500 to-cyan-500",
      icon: FiAward
    },
    {
      id: 4,
      title: "SIH Hackathon",
      event: "SIH Internal Hackathon",
      date: "2025",
      description: "Selected among top 15 teams for Smart India Hackathon with Fieldsense agriculture project",
      fullDescription: "Developed 'Fieldsense' - an innovative agriculture monitoring solution for Smart India Hackathon (SIH). Created from internal college hackathon selection among numerous teams. Project focuses on real-time field monitoring, crop health analysis, and farmer decision support. Received official college certificate for selection.",
      category: "Hackathon",
      skills: ["Full-Stack Development", "Agriculture Tech", "Problem Solving", "Teamwork", "Rapid Prototyping"],
      certificateUrl: "/images/achievements/sih-fieldsense-certificate.png",
      images: [
        { url: "/images/achievements/fieldsense-demo.jpg", caption: "Fieldsense project demo" },
        { url: "/images/achievements/sih-team.jpg", caption: "SIH selected team" }
      ],
      impact: [
        { value: "Top 15", label: "Selected Teams" },
        { value: "SIH 2025", label: "National Qualifier" }
      ],
      projectLink: "#projects",
      badgeColor: "from-emerald-500 to-teal-500",
      icon: FiTrendingUp
    },
    {
      id: 5,
      title: "InnovateX Finalist",
      event: "InnovateX Challenge",
      date: "2025",
      description: "Finalist at InnovateX for a research-driven, real-world problem solution",
      fullDescription: "Selected as a finalist at InnovateX for developing a research-driven solution addressing a real-world problem. Led research, ideation, prototyping, and validation with stakeholders. The project combined unique ideas and rigorous investigation to demonstrate technical feasibility and social impact.",
      category: "Research & Innovation",
      skills: ["Research", "Prototyping", "User Research", "Problem Solving", "Presentation"],
      certificateUrl: "/images/achievements/innovatex-finalist-certificate.png",
      images: [
        { url: "/images/achievements/innovatex-poster.jpg", caption: "Project poster" },
        { url: "/images/achievements/innovatex-team.jpg", caption: "Team at InnovateX" }
      ],
      impact: [
        { value: "Finalist", label: "Position" },
        { value: "Research", label: "Approach" }
      ],
      badgeColor: "from-amber-500 to-yellow-400",
      icon: FiTrendingUp,
      projectLink: "#projects"
    },
    {
      id: 6,
      title: "Samsung Innovation Campus (SIC)",
      event: "Samsung Innovation Campus",
      date: "2025-26",
      description: "Selected for Samsung Innovation Campus; received a kit and completed Python training from basic to advanced.",
      fullDescription: "Selected for the Samsung Innovation Campus program where I received an official kit and completed an intensive curriculum covering Python from fundamentals to advanced topics, including data structures, algorithms, and applied projects. Worked through hands-on exercises with the kit and collaborative assignments to strengthen software problem-solving skills.",
      category: "Training",
      skills: ["Python", "Data Structures", "Algorithms", "Embedded Systems", "Problem Solving"],
      certificateUrl: "/images/achievements/sic-certificate.png",
      images: [
        { url: "/images/achievements/sic-kit.jpg", caption: "SIC hardware kit" },
        { url: "/images/achievements/sic-session.jpg", caption: "Training session" }
      ],
      impact: [
        { value: "Kit Provided", label: "Kit" },
        { value: "Advanced", label: "Python Level" }
      ],
      badgeColor: "from-blue-600 to-indigo-500",
      icon: FiTrendingUp
    }
  ];

  // Rest of your existing code remains exactly the same...
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % achievements.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isPaused, achievements.length]);

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? achievements.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % achievements.length);
  };

  const openModal = (achievement) => {
    setSelectedAchievement(achievement);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedAchievement(null);
    document.body.style.overflow = 'unset';
  };

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isModalOpen]);

  const currentAchievement = achievements[currentIndex];

  return (
    <>
      <section className="py-8 sm:py-12 bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-10"
          >
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 text-purple-700 dark:text-purple-300 text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              <FiAward className="w-3 h-3 sm:w-4 sm:h-4" />
              College Achievements
            </div>
            
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              Beyond the Classroom
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2">
              Recognition and achievements earned through active participation in college events and competitions
            </p>
          </motion.div>

          {/* Rest of your JSX remains EXACTLY THE SAME - Mobile-First Carousel Container, Navigation, Dots, etc. */}
          {/* Mobile-First Carousel Container */}
          <div 
            className="relative w-full max-w-xs sm:max-w-sm md:max-w-2xl lg:max-w-3xl mx-auto px-0 sm:px-8 md:px-16"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="relative">
              {/* FIXED: Mobile-Optimized Card Heights with Better Text Handling */}
              <div className="relative h-[500px] sm:h-[440px] md:h-[460px] lg:h-[480px] overflow-visible rounded-xl sm:rounded-2xl">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentIndex}
                    initial={{ x: 300, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -300, opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    className="absolute inset-0"
                  >
                    <motion.div 
                      className="h-full w-full bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden cursor-pointer"
                      onClick={() => openModal(currentAchievement)}
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                    >
                      
                      {/* Card Header */}
                      <div className={`relative h-24 sm:h-28 bg-gradient-to-br ${currentAchievement.badgeColor} flex items-center justify-center`}>
                        <div className="absolute inset-0 bg-black/10"></div>
                        
                        <div className="absolute inset-0">
                          <div className="absolute top-3 right-3 w-8 h-8 sm:w-12 sm:h-12 bg-white/10 rounded-full"></div>
                          <div className="absolute bottom-2 left-3 w-4 h-4 sm:w-6 sm:h-6 bg-white/10 rounded-full"></div>
                        </div>
                        
                        <div className="relative z-10 text-center">
                          <currentAchievement.icon className="w-8 h-8 sm:w-10 sm:h-10 text-white mx-auto mb-1 sm:mb-2" />
                          <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-semibold">
                            {currentAchievement.category}
                          </span>
                        </div>
                      </div>

                      {/* FIXED: Card Content with Better Mobile Text Handling */}
                      <div className="p-4 sm:p-5 h-[376px] sm:h-[312px] md:h-[332px] lg:h-[352px] flex flex-col">
                        <div className="mb-3 sm:mb-4 pb-2">
                          <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 dark:text-white mb-1 leading-tight pb-1">
                            {currentAchievement.title}
                          </h3>
                          <p className="text-xs sm:text-sm font-medium text-purple-600 dark:text-purple-400 mb-1 pb-1">
                            {currentAchievement.event}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 pb-1">
                            {currentAchievement.date}
                          </p>
                        </div>

                        {/* Fixed description with better responsive handling */}
                        <p className="text-gray-600 dark:text-gray-300 mb-3 sm:mb-4 leading-relaxed text-xs sm:text-sm break-words pb-2">
                          {currentAchievement.description}
                        </p>

                        {/* Skills Section with better spacing */}
                        <div className="mb-3 sm:mb-4 flex-grow pb-2">
                          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 pb-1">
                            Key Skills
                          </h4>
                          <div className="flex flex-wrap gap-1.5 pb-2">
                            {currentAchievement.skills.slice(0, 3).map((skill, skillIndex) => (
                              <span
                                key={skillIndex}
                                className="px-2 py-1 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 text-purple-700 dark:text-purple-300 rounded-md text-xs font-medium border border-purple-200 dark:border-purple-700/50"
                              >
                                {skill}
                              </span>
                            ))}
                            {currentAchievement.skills.length > 3 && (
                              <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-md text-xs font-medium">
                                +{currentAchievement.skills.length - 3}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* FIXED: Action Buttons with Guaranteed Space */}
                        <div className="flex flex-col sm:flex-row gap-2 mt-auto pt-3 pb-4">
                          <motion.button
                            onClick={(e) => {
                              e.stopPropagation();
                              openModal(currentAchievement);
                            }}
                            className="w-full flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-3 sm:px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-200 shadow-lg"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <FiEye className="w-4 h-4" />
                            View Details
                          </motion.button>
                          
                          <motion.button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(currentAchievement.certificateUrl, '_blank');
                            }}
                            className="w-full sm:w-auto px-3 sm:px-4 py-2.5 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 transition-all duration-200 inline-flex items-center justify-center gap-2 shadow-lg text-xs sm:text-sm"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <FiImage className="w-4 h-4" />
                            <span className="sm:hidden">Certificate</span>
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation Arrows - Hidden on mobile */}
              <button
                onClick={handlePrevious}
                className="hidden sm:flex absolute -left-8 md:-left-16 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-full shadow-lg border border-gray-200 dark:border-gray-600 items-center justify-center transition-all duration-200 z-10"
                aria-label="Previous achievement"
              >
                <FiChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-gray-600 dark:text-gray-300" />
              </button>

              <button
                onClick={handleNext}
                className="hidden sm:flex absolute -right-8 md:-right-16 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-full shadow-lg border border-gray-200 dark:border-gray-600 items-center justify-center transition-all duration-200 z-10"
                aria-label="Next achievement"
              >
                <FiChevronRight className="w-4 h-4 md:w-5 md:h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
          </div>

          {/* Dots Indicator - Now shows 4 dots */}
          <div className="flex justify-center mt-6 gap-2">
            {achievements.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-purple-600 w-4 sm:w-6'
                    : 'bg-gray-300 dark:bg-gray-600 hover:bg-purple-400'
                }`}
                aria-label={`Go to achievement ${index + 1}`}
              />
            ))}
          </div>

          {/* Mobile Navigation Hint */}
          <div className="flex sm:hidden justify-center mt-4">
            <span className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-3 py-1 rounded-full border border-gray-200 dark:border-gray-700">
              Tap dots to navigate
            </span>
          </div>
        </div>
      </section>

      {/* FULL FEATURED MODAL - Works perfectly with new achievement */}
      <AnimatePresence>
        {isModalOpen && selectedAchievement && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-md md:max-w-2xl max-h-[90vh] overflow-y-auto relative"
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gray-100 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full flex items-center justify-center transition-all duration-200 z-20 group shadow-lg"
                aria-label="Close modal"
                type="button"
              >
                <FiX className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-600 dark:text-gray-300 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors duration-200" />
              </button>

              {/* Modal Header */}
              <div className={`relative h-32 sm:h-40 md:h-48 bg-gradient-to-br ${selectedAchievement.badgeColor} rounded-t-xl sm:rounded-t-2xl overflow-hidden`}>
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute inset-0">
                  <div className="absolute top-8 right-8 w-32 h-32 bg-white/10 rounded-full"></div>
                  <div className="absolute bottom-8 left-8 w-16 h-16 bg-white/10 rounded-full"></div>
                  <div className="absolute top-16 left-16 w-8 h-8 bg-white/20 rounded-full"></div>
                </div>

                <div className="relative z-10 flex flex-col items-center justify-center h-full text-center p-4 sm:p-6 md:p-8">
                  <selectedAchievement.icon className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 text-white mb-2 sm:mb-3 md:mb-4" />
                  <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-1 sm:mb-2">
                    {selectedAchievement.title}
                  </h1>
                  <p className="text-white/90 text-sm sm:text-base md:text-lg">
                    {selectedAchievement.event}
                  </p>
                  <span className="mt-2 sm:mt-3 px-3 sm:px-4 py-1 sm:py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs sm:text-sm font-semibold">
                    {selectedAchievement.category}
                  </span>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-4 sm:p-6 md:p-8">
                {/* Date */}
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-4 sm:mb-6">
                  <FiCalendar className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-medium text-sm sm:text-base">{selectedAchievement.date}</span>
                </div>

                {/* Full Description */}
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3">
                    About This Achievement
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm sm:text-base">
                    {selectedAchievement.fullDescription || selectedAchievement.description}
                  </p>
                </div>

                {/* Achievement Images/Photos */}
                {selectedAchievement.images && selectedAchievement.images.length > 0 && (
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3">
                      Photos & Highlights
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {selectedAchievement.images.map((image, index) => (
                        <img
                          key={index}
                          src={image.url}
                          alt={image.caption || `Achievement image ${index + 1}`}
                          className="w-full h-24 sm:h-32 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* All Skills */}
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3">
                    Skills & Competencies
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedAchievement.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-xs sm:text-sm font-medium border border-purple-200 dark:border-purple-700/50"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Impact/Stats */}
                {selectedAchievement.impact && (
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3">
                      Impact & Results
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedAchievement.impact.map((stat, index) => (
                        <div key={index} className="text-center p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400">
                            {stat.value}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                            {stat.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <motion.button
                    onClick={() => window.open(selectedAchievement.certificateUrl, '_blank')}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 sm:px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-sm sm:text-base"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FiImage className="w-4 h-4 sm:w-5 sm:h-5" />
                    View Certificate
                  </motion.button>
                  
                  {selectedAchievement.projectLink && (
                    <motion.a
                      href={selectedAchievement.projectLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 sm:px-6 py-3 rounded-xl font-semibold border border-gray-300 dark:border-gray-600 transition-all duration-200 text-sm sm:text-base"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <FiExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                      View Project
                    </motion.a>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CollegeAchievements;
